/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefObject } from 'preact';
import { TagCloudContextMenuContext, TagCloudContextMenuConfig, ItemInfo } from './tagCloud.type';
import { type gestureTypes, useContextMenu } from '#hooks/PRIVATE_useVisContextMenu';

type Props<D> = {
  focusedItemInfo: ItemInfo;
  items: D[];
  rootRef: RefObject<HTMLDivElement>;
  contextMenuConfig?: TagCloudContextMenuConfig<D>;
  onContextMenuDismissed?: (gesture: gestureTypes) => void;
  getItemInfo: (element: HTMLElement) =>
    | {
        idx: number;
      }
    | undefined;
};

export const useTagCloudContextMenu = <D>({
  getItemInfo,
  focusedItemInfo,
  rootRef,
  contextMenuConfig,
  items,
  onContextMenuDismissed
}: Props<D>) => {
  const { contextMenuProps, contextMenuContent } = useContextMenu<TagCloudContextMenuContext<D>>({
    onContextMenuHandler: ({ gesture, target }) => {
      if (gesture === 'keyboard') {
        const idx = focusedItemInfo.idx;
        const elem = rootRef.current?.querySelector(`[data-idx="${idx}"]`) || null;
        const data = items[idx];
        const context = {
          type: 'item' as const,
          data
        };
        return {
          type: 'element',
          context,
          elem
        };
      }

      const idx = getItemInfo(target as HTMLElement)?.idx;
      const data = idx !== undefined ? items[idx] : null;
      let context;
      if (data) {
        context = {
          type: 'item' as const,
          data
        };
      } else {
        context = { type: 'background' as const };
      }
      return {
        type: 'pointer',
        context
      };
    },
    contextMenuOptions: {
      isDisabled: !contextMenuConfig //If there is no context menu renderer we disable the hook
    },
    rootRef,
    contextMenuConfig,
    onContextMenuDismissed
  });

  return { contextMenuContent, contextMenuProps };
};
